import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowRight, TrendingUp, ShieldAlert, ShieldCheck, Users, Star, PieChart, Zap } from 'lucide-react';
import SocialFeed from '@/components/SocialFeed';
import BattleWidget from '@/components/BattleWidget';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';

function RebalanceButton({ onDone }) {
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState(null);
  const [schedule, setSchedule] = useState({ frequency: 'off', next_run: null });
  const [busy, setBusy] = useState(false);
  const loadPlan = async () => {
    const [p, s] = await Promise.all([
      api.get('/portfolio/rebalance'),
      api.get('/portfolio/schedule').catch(() => ({ data: { frequency: 'off' } })),
    ]);
    setPlan(p.data);
    setSchedule(s.data);
  };
  useEffect(() => { if (open) loadPlan(); }, [open]);
  const execute = async () => {
    setBusy(true);
    try {
      const r = await api.post('/portfolio/rebalance/execute');
      toast.success(`Rebalanced ${r.data.executed} trades — grade improved`);
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const setCadence = async (frequency) => {
    try {
      const r = await api.post('/portfolio/schedule', { frequency });
      setSchedule(r.data);
      toast.success(frequency === 'off' ? 'Auto-rebalance disabled' : `Auto-rebalance set to ${frequency}`);
    } catch (e) { toast.error('Failed'); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="mt-3 w-full border-[#FCD535]/40 bg-transparent text-[#FCD535] hover:bg-[#FCD535]/10 hover:text-[#FCD535] rounded-sm" data-testid="rebalance-btn">
          <Zap className="w-3.5 h-3.5 mr-1.5" /> Auto-Rebalance
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm max-w-lg">
        <DialogHeader><DialogTitle>Portfolio Rebalance</DialogTitle></DialogHeader>
        {!plan ? (
          <div className="text-[#848E9C] text-sm">Loading plan…</div>
        ) : (
          <div className="space-y-4">
            {/* Cadence */}
            <div className="border border-[#2B3139] bg-[#0B0E11] p-3">
              <div className="text-xs uppercase tracking-wider text-[#848E9C] mb-2">Auto-Rebalance Schedule</div>
              <div className="flex gap-1.5">
                {['off','weekly','monthly'].map(f => (
                  <button key={f} onClick={() => setCadence(f)} data-testid={`cadence-${f}`}
                    className={`flex-1 px-3 py-1.5 text-xs rounded-sm capitalize ${schedule.frequency === f ? 'bg-[#FCD535] text-[#0B0E11] font-semibold' : 'bg-[#181A20] text-[#B7BDC6] hover:text-white'}`}>
                    {f}
                  </button>
                ))}
              </div>
              {schedule.next_run && (
                <div className="text-[10px] text-[#5E6673] mt-2">Next auto-run: <span className="text-[#FCD535] font-mono">{new Date(schedule.next_run).toLocaleDateString()}</span></div>
              )}
            </div>
            {/* Plan */}
            {plan.trades.length === 0 ? (
              <div className="text-[#848E9C] text-sm">{plan.message || 'Portfolio is already balanced.'}</div>
            ) : (
              <>
                <div className="text-sm text-[#B7BDC6]">
                  Move from grade <span className="font-mono text-[#F6465D]">{plan.current_grade}</span> toward <span className="font-mono text-[#0ECB81]">{plan.target_grade}</span> — <span className="text-white">{plan.assets.length}</span> assets ~${plan.per_asset_target_usd} each.
                </div>
                <div className="border border-[#2B3139] bg-[#0B0E11] max-h-56 overflow-y-auto" data-testid="rebalance-plan">
                  {plan.trades.map((t, i) => (
                    <div key={i} className="grid grid-cols-12 px-3 py-2 border-b border-[#2B3139]/60 items-center text-xs">
                      <div className={`col-span-2 uppercase font-semibold ${t.action === 'buy' ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>{t.action}</div>
                      <div className="col-span-2 text-white">{t.symbol}</div>
                      <div className="col-span-4 text-right font-mono text-[#EAECEF]">{t.coin_amount}</div>
                      <div className="col-span-4 text-right font-mono text-[#B7BDC6]">${t.usd_value}</div>
                    </div>
                  ))}
                </div>
                <Button onClick={execute} disabled={busy} className="w-full bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold" data-testid="execute-rebalance-btn">
                  {busy ? 'Executing…' : `Execute ${plan.trades.length} trades now`}
                </Button>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


export default function Dashboard() {
  const { user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [trades, setTrades] = useState([]);
  const [stakes, setStakes] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [pnl, setPnl] = useState({ points: [], current_value: 0, synthetic: false });
  const [range, setRange] = useState(30);
  const [rbHistory, setRbHistory] = useState({ history: [], total_drift_saved_pct: 0 });

  useEffect(() => {
    (async () => {
      const [w, t, s, m, p, wl, rbh] = await Promise.all([
        api.get('/wallet'),
        api.get('/trades'),
        api.get('/staking/mine'),
        api.get('/markets'),
        api.get(`/portfolio/history_v2?days=${range}`).catch(() => api.get(`/portfolio/history?days=${range}`)),
        api.get('/watchlist').catch(() => ({ data: { coins: [] } })),
        api.get('/portfolio/rebalance/history').catch(() => ({ data: { history: [], total_drift_saved_pct: 0 } })),
      ]);
      setWallet(w.data);
      setTrades(t.data.trades.slice(0, 8));
      setStakes(s.data.stakes.filter(x => x.status === 'active'));
      setMarkets(m.data.markets.slice(0, 5));
      setPnl(p.data);
      setWatchlist(wl.data.coins || []);
      setRbHistory(rbh.data);
    })();
  }, [range]);

  const topAssets = wallet?.balances?.slice(0, 4) || [];
  const totalStakeUsd = stakes.reduce((sum, s) => {
    const mk = markets.find(m => m.symbol.toUpperCase() === s.symbol);
    return sum + (s.amount * (mk?.current_price || (s.symbol === 'USDT' ? 1 : 0)));
  }, 0);

  return (
    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-white text-4xl font-bold tracking-tighter">Welcome back, {user?.name?.split(' ')[0]}</h1>
          <p className="text-[#848E9C] text-sm mt-1">Here&apos;s a snapshot of your portfolio and activity.</p>
        </div>
        <div className="flex items-center gap-2">
          {user?.kyc_status === 'verified' ? (
            <div className="flex items-center gap-2 text-[#0ECB81] text-sm"><ShieldCheck className="w-4 h-4" /> KYC Verified</div>
          ) : (
            <Link to="/kyc">
              <Button variant="outline" size="sm" className="border-[#FCD535]/40 bg-transparent text-[#FCD535] hover:bg-[#FCD535]/10 hover:text-[#FCD535] rounded-sm" data-testid="dash-verify-btn">
                <ShieldAlert className="w-4 h-4 mr-1.5" /> Complete KYC
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Overview grid */}
      <div className="grid grid-cols-12 gap-4 mb-6">
        <div className="col-span-12 lg:col-span-8 bg-gradient-to-br from-[#181A20] to-[#0B0E11] border border-[#2B3139] p-6 sm:p-8">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#848E9C]">Total Portfolio Value</div>
              <div className="font-mono text-white text-4xl sm:text-5xl font-medium tracking-tight mt-2" data-testid="dash-total">
                ${wallet?.total_usd?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || '0.00'}
              </div>
              {pnl.points.length >= 2 && (() => {
                const first = pnl.points[0].v;
                const last = pnl.points[pnl.points.length - 1].v;
                const delta = last - first;
                const pct = first > 0 ? (delta / first) * 100 : 0;
                const up = delta >= 0;
                return (
                  <div className={`font-mono text-sm mt-1 ${up ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>
                    {up ? '+' : ''}${Math.abs(delta).toFixed(2)} ({up ? '+' : ''}{pct.toFixed(2)}%) · {range}d
                    {pnl.synthetic && <span className="text-[#5E6673] ml-2">(sample data)</span>}
                  </div>
                );
              })()}
            </div>
            <div className="flex gap-1 border border-[#2B3139] p-0.5 rounded-sm" data-testid="pnl-range">
              {[7, 30, 90, 365].map(d => (
                <button key={d} onClick={() => setRange(d)}
                  className={`px-3 py-1 text-xs font-mono rounded-sm ${range === d ? 'bg-[#FCD535] text-[#0B0E11]' : 'text-[#848E9C] hover:text-white'}`}
                  data-testid={`range-${d}`}>{d === 365 ? '1Y' : `${d}D`}</button>
              ))}
            </div>
          </div>
          <div className="h-[220px]" data-testid="pnl-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={pnl.points.map(p => ({ t: p.t, v: p.v }))}>
                <defs>
                  <linearGradient id="pnlGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FCD535" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#FCD535" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="t" hide />
                <YAxis domain={['dataMin', 'dataMax']} tick={{ fill: '#5E6673', fontSize: 10, fontFamily: 'JetBrains Mono' }} axisLine={false} tickLine={false} width={60} tickFormatter={(v) => `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`} />
                <Tooltip
                  contentStyle={{ background: '#0B0E11', border: '1px solid #2B3139', borderRadius: 0, fontFamily: 'JetBrains Mono' }}
                  labelFormatter={(t) => new Date(t).toLocaleString()}
                  formatter={(v) => [`$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`, 'Value']}
                />
                <Area dataKey="v" stroke="#FCD535" strokeWidth={2} fill="url(#pnlGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 border-t border-[#2B3139] pt-4">
            {topAssets.map(a => (
              <div key={a.symbol}>
                <div className="text-xs uppercase tracking-wider text-[#848E9C]">{a.symbol}</div>
                <div className="font-mono text-white text-lg mt-1">{a.amount.toFixed(4)}</div>
                <div className="font-mono text-[#848E9C] text-xs mt-0.5">${a.usd_value.toFixed(2)}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="col-span-12 lg:col-span-4 grid gap-4">
          <div className="bg-[#181A20] border border-[#2B3139] p-6">
            <div className="text-xs uppercase tracking-widest text-[#848E9C]">Active Stakes</div>
            <div className="font-mono text-white text-3xl mt-2">{stakes.length}</div>
            <div className="text-xs text-[#0ECB81] font-mono mt-1">≈ ${totalStakeUsd.toFixed(2)} locked</div>
            <Link to="/staking">
              <Button size="sm" variant="outline" className="mt-4 border-[#2B3139] bg-transparent text-white hover:bg-[#2B3139] rounded-sm" data-testid="dash-staking-btn">
                Manage <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </Link>
          </div>
          <div className="bg-[#181A20] border border-[#2B3139] p-6">
            <div className="flex items-center gap-2 text-[#848E9C] text-xs uppercase tracking-widest">
              <PieChart className="w-3.5 h-3.5" /> Diversification
            </div>
            <div className="flex items-baseline gap-3 mt-2">
              <div className="font-mono text-4xl" style={{ color: wallet?.diversification?.color || '#848E9C' }}>
                {wallet?.diversification?.grade || '—'}
              </div>
              <div className="text-xs text-[#848E9C]">{wallet?.diversification?.asset_count || 0} assets</div>
            </div>
            <div className="text-xs text-[#B7BDC6] mt-1">{wallet?.diversification?.description || 'Add more assets to improve'}</div>
            {wallet?.diversification?.suggestions?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-[#2B3139]/60 space-y-1.5" data-testid="div-suggestions">
                {wallet.diversification.suggestions.map(s => (
                  <Link key={s.symbol} to="/markets" className="flex items-center gap-1.5 text-[10px] text-[#848E9C] hover:text-[#FCD535]">
                    <span className="inline-block w-1 h-1 rounded-full bg-[#FCD535]" />
                    <span className="flex-1">{s.message}</span>
                  </Link>
                ))}
              </div>
            )}
            {wallet && wallet.total_usd > 100 && wallet.diversification?.grade !== 'A+' && wallet.diversification?.grade !== 'A' && (
              <RebalanceButton onDone={() => window.location.reload()} />
            )}
          </div>
          <div className="bg-[#181A20] border border-[#2B3139] p-6">
            <div className="text-xs uppercase tracking-widest text-[#848E9C]">Recent Trades</div>
            <div className="font-mono text-white text-3xl mt-2">{trades.length}</div>
            <Link to="/markets">
              <Button size="sm" className="mt-4 bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold rounded-sm" data-testid="dash-trade-btn">
                Trade Now <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </Link>
          </div>
          <div className="bg-gradient-to-br from-[#FCD535]/15 to-transparent border border-[#FCD535]/30 p-6">
            <div className="flex items-center gap-2 text-[#848E9C] text-xs uppercase tracking-widest"><Users className="w-3.5 h-3.5" /> Refer & Earn</div>
            <div className="font-mono text-[#FCD535] text-2xl mt-2">+100 USDT</div>
            <div className="text-xs text-[#848E9C]">per friend who signs up</div>
            <Link to="/referrals">
              <Button size="sm" variant="outline" className="mt-3 border-[#FCD535]/40 bg-transparent text-[#FCD535] hover:bg-[#FCD535]/10 hover:text-[#FCD535] rounded-sm" data-testid="dash-referrals-btn">
                Get your link <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </Link>
          </div>
          <BattleWidget />
        </div>
      </div>

      {/* Watchlist + Recent trades */}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-7 border border-[#2B3139] bg-[#181A20]">
          <div className="px-4 py-3 border-b border-[#2B3139] flex items-center justify-between">
            <div className="text-white font-semibold flex items-center gap-2">
              <Star className="w-4 h-4 text-[#FCD535]" fill={watchlist.length ? '#FCD535' : 'none'} />
              {watchlist.length > 0 ? 'My Watchlist' : 'Trending'}
            </div>
            <Link to="/markets" className="text-[#FCD535] text-xs hover:underline">See all →</Link>
          </div>
          {(watchlist.length > 0 ? watchlist : markets).map(c => {
            const up = c.price_change_percentage_24h >= 0;
            return (
              <Link key={c.id} to={`/trade/${c.id}`} className="grid grid-cols-12 items-center px-4 py-3 border-b border-[#2B3139]/60 hover:bg-[#2B3139]">
                <div className="col-span-4 flex items-center gap-2">
                  <img src={c.image} alt="" className="w-5 h-5" />
                  <span className="text-white text-sm">{c.name}</span>
                  <span className="text-[#848E9C] text-xs uppercase">{c.symbol}</span>
                </div>
                <div className="col-span-3 text-right font-mono text-white text-sm">${c.current_price?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                <div className={`col-span-3 text-right font-mono text-sm ${up ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>
                  {up ? '+' : ''}{c.price_change_percentage_24h?.toFixed(2)}%
                </div>
                <div className="col-span-2 flex justify-end">
                  <ResponsiveContainer width={80} height={30}>
                    <AreaChart data={(c.sparkline_in_7d?.price || []).map((p, i) => ({ i, p }))}>
                      <Area dataKey="p" stroke={up ? '#0ECB81' : '#F6465D'} fill={up ? '#0ECB81' : '#F6465D'} fillOpacity={0.1} strokeWidth={1.5} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </Link>
            );
          })}
          {watchlist.length === 0 && (
            <div className="px-4 py-2 text-[10px] text-[#5E6673] text-center italic">Tap the star on any coin in Markets to build your personal shortlist</div>
          )}
        </div>
        <div className="col-span-12 lg:col-span-5 border border-[#2B3139] bg-[#181A20]">
          <div className="px-4 py-3 border-b border-[#2B3139] text-white font-semibold">Recent Trades</div>
          {trades.length === 0 ? (
            <div className="p-6 text-center text-[#848E9C] text-sm">No trades yet.</div>
          ) : trades.map(t => (
            <div key={t.trade_id} className="grid grid-cols-12 px-4 py-3 border-b border-[#2B3139]/60 items-center text-sm">
              <div className={`col-span-2 uppercase text-xs font-semibold ${t.side === 'buy' ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>{t.side}</div>
              <div className="col-span-3 text-white">{t.symbol}</div>
              <div className="col-span-4 text-right font-mono text-[#EAECEF]">{t.amount.toFixed(6)}</div>
              <div className="col-span-3 text-right font-mono text-[#848E9C] text-xs">${t.total_usd.toFixed(2)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Social feed */}
      <div className="mt-6">
        <SocialFeed compact />
      </div>

      {/* Rebalance history retrospective */}
      {rbHistory.history.length > 0 && (
        <div className="mt-6 border border-[#2B3139] bg-[#181A20]" data-testid="rebalance-history">
          <div className="px-4 py-3 border-b border-[#2B3139] flex items-center justify-between">
            <div className="text-white font-semibold flex items-center gap-2">
              <Zap className="w-4 h-4 text-[#FCD535]" />
              Auto-Rebalance History
            </div>
            <div className="text-xs text-[#B7BDC6]">
              You've saved <span className="font-mono text-[#0ECB81]">{rbHistory.total_drift_saved_pct}%</span> from portfolio drift
            </div>
          </div>
          {rbHistory.history.slice(0, 6).map(r => (
            <div key={r.run_id} className="grid grid-cols-12 px-4 py-2.5 border-b border-[#2B3139]/60 items-center text-sm">
              <div className="col-span-4 text-[#EAECEF]">{new Date(r.run_at).toLocaleString()}</div>
              <div className="col-span-3 text-[#B7BDC6]">{r.trades_count} trades</div>
              <div className="col-span-3 text-right"><span className="font-mono" style={{ color: r.after_grade?.startsWith('A') ? '#0ECB81' : '#FCD535' }}>Grade {r.after_grade}</span></div>
              <div className="col-span-2 text-right font-mono text-[#0ECB81]">+{r.drift_saved_pct}%</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
